/**
 * The period model every attendance report is asked for.
 *
 * All four attendance routes take the SAME two mutually exclusive shapes — `?month=`
 * or `?from=&to=` — and the server states in its own docs that `month` is ignored when
 * a range is present. Modelling that as a discriminated union rather than three
 * optional strings is what stops a screen from sending both and quietly getting the
 * range while its title says the month.
 *
 * The 366-day ceiling is the server's (`422` past it, "a longer span is a data export,
 * and this refuses it"). It is enforced here too, before the request, because a report
 * screen that lets someone pick 1 января 2024 – сегодня and then shows a raw 422 has
 * taught them nothing about why.
 *
 * Everything is locale-independent: `YYYY-MM` / `YYYY-MM-DD` strings built from local
 * calendar fields, never `toISOString()` (which converts to UTC first and hands back
 * yesterday for anyone east of Greenwich — Tashkent is UTC+5, so that is every user
 * of this product before 05:00).
 */

import type {ParamCodec} from '@/shared/lib/use-param-state';

export type Period =
  | {kind: 'month'; month: string}
  | {kind: 'range'; from: string; to: string};

/** The presets the picker offers, in the order it offers them. */
export type PeriodPresetId =
  | 'today'
  | 'yesterday'
  | 'week'
  | 'month'
  | 'prevMonth'
  | 'days30'
  | 'quarter'
  | 'year';

export const PERIOD_PRESETS: readonly PeriodPresetId[] = [
  'today',
  'yesterday',
  'week',
  'month',
  'prevMonth',
  'days30',
  'quarter',
  'year',
];

/** The server's hard ceiling on `from`..`to`, inclusive. */
export const MAX_PERIOD_DAYS = 366;

const pad2 = (n: number): string => String(n).padStart(2, '0');

const DAY_MS = 24 * 60 * 60 * 1000;

/** A local calendar date → "YYYY-MM-DD". Never via toISOString(); see the module note. */
export function isoDate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** A local calendar date → "YYYY-MM". */
export function isoMonth(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;
}

/** "YYYY-MM-DD" → a Date at local midnight. Invalid input → today. */
export function parseIsoDate(value: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) {
    return new Date();
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** "YYYY-MM" → a Date at local midnight on the 1st. Invalid input → this month. */
export function parseIsoMonth(value: string): Date {
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, 1);
}

/** The month the app opens on. */
export function currentMonth(now: Date = new Date()): Period {
  return {kind: 'month', month: isoMonth(now)};
}

/** "2026-08" shifted by whole months. `shiftMonth('2026-01', -1)` → "2025-12". */
export function shiftMonth(month: string, delta: number): string {
  const d = parseIsoMonth(month);
  d.setMonth(d.getMonth() + delta);
  return isoMonth(d);
}

function daysAgo(n: number, now: Date): Date {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  d.setDate(d.getDate() - n);
  return d;
}

/**
 * A preset → a concrete period. `now` is a parameter so this is testable and so a
 * screen can pin "today" for the life of a render instead of re-reading the clock.
 */
export function periodFromPreset(id: PeriodPresetId, now: Date = new Date()): Period {
  const today = isoDate(now);
  switch (id) {
    case 'today':
      return {kind: 'range', from: today, to: today};
    case 'yesterday': {
      const d = isoDate(daysAgo(1, now));
      return {kind: 'range', from: d, to: d};
    }
    case 'week':
      // Seven days ENDING today, not "this calendar week": "как прошла неделя" is a
      // trailing window, and a calendar week is one day long on a Monday morning.
      return {kind: 'range', from: isoDate(daysAgo(6, now)), to: today};
    case 'month':
      return {kind: 'month', month: isoMonth(now)};
    case 'prevMonth':
      return {kind: 'month', month: shiftMonth(isoMonth(now), -1)};
    case 'days30':
      return {kind: 'range', from: isoDate(daysAgo(29, now)), to: today};
    case 'quarter':
      return {kind: 'range', from: isoDate(daysAgo(89, now)), to: today};
    case 'year':
      // 365 days, not 366: the ceiling is inclusive on both ends, so a 366-day
      // window is already the maximum and leaves no room for an off-by-one.
      return {kind: 'range', from: isoDate(daysAgo(364, now)), to: today};
  }
}

/** Which preset (if any) a period is exactly equal to — for highlighting the chip. */
export function matchPreset(
  period: Period,
  now: Date = new Date(),
): PeriodPresetId | null {
  for (const id of PERIOD_PRESETS) {
    const candidate = periodFromPreset(id, now);
    if (candidate.kind !== period.kind) {
      continue;
    }
    if (candidate.kind === 'month' && period.kind === 'month') {
      if (candidate.month === period.month) {
        return id;
      }
    } else if (candidate.kind === 'range' && period.kind === 'range') {
      if (candidate.from === period.from && candidate.to === period.to) {
        return id;
      }
    }
  }
  return null;
}

/** Inclusive day count of a period. A month resolves through its own calendar length. */
export function periodLength(period: Period): number {
  const {from, to} = periodBounds(period);
  const ms = parseIsoDate(to).getTime() - parseIsoDate(from).getTime();
  return Math.floor(ms / DAY_MS) + 1;
}

/** The first and last day a period covers, whichever shape it is. */
export function periodBounds(period: Period): {from: string; to: string} {
  if (period.kind === 'range') {
    return {from: period.from, to: period.to};
  }
  const start = parseIsoMonth(period.month);
  // Day 0 of the NEXT month is the last day of this one — no month-length table.
  const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
  return {from: isoDate(start), to: isoDate(end)};
}

/**
 * The query parameters for a period. A month sends ONLY `month` — sending `from`/`to`
 * alongside it would win and silently redefine the period the screen thinks it asked
 * for.
 */
export function periodParams(period: Period): Record<string, string> {
  return period.kind === 'month'
    ? {month: period.month}
    : {from: period.from, to: period.to};
}

/**
 * A period as one short string: `m:2026-08` or `r:2026-07-14:2026-08-12`.
 *
 * Two jobs, and the second is why it is not just `JSON.stringify`:
 *
 *   - react-query cache keys, where a stable primitive beats an object.
 *   - **route params.** With web linking enabled (app/linking.ts) every route param
 *     is serialised into the URL, and an object becomes the literal string
 *     `[object Object]` — so a reloaded or shared report link would restore a period
 *     that cannot be parsed. Keeping the param a string keeps the URL both correct
 *     and readable: `/reports/employee/{id}?period=m:2026-08`.
 */
export function periodKey(period: Period): string {
  return period.kind === 'month'
    ? `m:${period.month}`
    : `r:${period.from}:${period.to}`;
}

/**
 * The inverse of {@link periodKey}. Anything unrecognised — a truncated link, an old
 * bookmark, a hand-edited URL — falls back to the current month rather than throwing:
 * a report that opens on the wrong period is recoverable in one tap; a screen that
 * crashes on someone's shared link is not.
 */
export function parsePeriodKey(key: string, now: Date = new Date()): Period {
  const month = /^m:(\d{4}-\d{2})$/.exec(key);
  if (month) {
    return {kind: 'month', month: month[1]};
  }
  const range = /^r:(\d{4}-\d{2}-\d{2}):(\d{4}-\d{2}-\d{2})$/.exec(key);
  if (range) {
    return clampPeriod({kind: 'range', from: range[1], to: range[2]});
  }
  return currentMonth(now);
}

/**
 * Период как параметр адреса.
 *
 * Тот же ключ, которым период уже ездит между экранами (`m:2026-08`,
 * `r:2026-08-01:2026-08-07`), — так что ссылка на отчёт читается глазом и переживает
 * F5. Период по умолчанию в адрес не пишется: `/attendance` должен оставаться
 * `/attendance`, а не превращаться в `/attendance?period=m:2026-09`.
 *
 * `fallback` НЕ передают там, где период — обязательная часть вопроса, а не умолчание:
 * отчёт по сотруднику и по отделу всегда открывают за конкретный период, полученный от
 * того экрана, откуда в него вошли. Опусти здесь умолчание — и возврат к периоду, с
 * которого вошли, стёр бы его из адреса, а перезагрузка подставила бы вместо него
 * текущий месяц, то есть показала бы не тот отчёт, что был на экране.
 */
export function periodParam(now: Date, fallback?: Period): ParamCodec<Period> {
  // `null`, а не `undefined`: с ним `key === empty` не совпадёт ни при каком периоде,
  // и ключ пишется в адрес всегда.
  const empty = fallback ? periodKey(fallback) : null;
  return {
    parse: raw => parsePeriodKey(raw, now),
    format: value => {
      const key = periodKey(value);
      return key === empty ? undefined : key;
    },
  };
}

export type PeriodProblem = 'reversed' | 'tooLong';

/**
 * What is wrong with a period the user assembled by hand, or null if nothing is.
 * A month is always valid — it cannot be reversed and no calendar month is 367 days.
 */
export function validatePeriod(period: Period): PeriodProblem | null {
  if (period.kind === 'month') {
    return null;
  }
  if (period.from > period.to) {
    return 'reversed';
  }
  if (periodLength(period) > MAX_PERIOD_DAYS) {
    return 'tooLong';
  }
  return null;
}

/**
 * The nearest period the server would accept: a reversed range is swapped (the user
 * clearly meant the span, not the order), and an over-long one keeps its END and moves
 * its start — "the most recent 366 days of what you asked for" is closer to the intent
 * than "the first 366".
 */
export function clampPeriod(period: Period): Period {
  if (period.kind === 'month') {
    return period;
  }
  const from = period.from > period.to ? period.to : period.from;
  const to = period.from > period.to ? period.from : period.to;
  const span = periodLength({kind: 'range', from, to});
  if (span <= MAX_PERIOD_DAYS) {
    return {kind: 'range', from, to};
  }
  const start = parseIsoDate(to);
  start.setDate(start.getDate() - (MAX_PERIOD_DAYS - 1));
  return {kind: 'range', from: isoDate(start), to};
}

/**
 * Период, обрезанный сегодняшним днём.
 *
 * ## Зачем
 *
 * Движок явки проходит по КАЛЕНДАРНЫМ дням запрошенного периода и ставит `absent`
 * каждому рабочему дню, в который никто не отметился. Для завтрашнего дня это верно
 * буквально и неверно по смыслу: человек не пропустил день, который ещё не наступил.
 * Спросив «август» двадцать шестого числа, мы получали пять красных клеток в календаре,
 * пять неявок в счётчике и явку 76% вместо 100% — и так у всех и весь месяц, пока он не
 * кончится.
 *
 * Чинится это здесь, а не в отрисовке, потому что будущее приезжает не только клетками:
 * `summary.absent_days` считает те же дни, и на сводке компании (`include_days=false`)
 * дней нет вовсе — исправить их на экране было бы нечем. Не спросив про будущее, мы не
 * получаем его ни в одном из ответов: ни в днях, ни в счётчиках, ни в графике, ни в
 * ведомости, ни в серверных выгрузках.
 *
 * ## Что происходит с формой периода
 *
 * Текущий месяц перестаёт быть `month=` и становится `from=…&to=сегодня`. Это не
 * побочный эффект, а суть: отчёт покрывает месяц ПО СЕГОДНЯ, и `month` в ответе
 * приходит пустым — экраны сами начинают называть период двумя датами, потому что
 * читают его из ответа.
 *
 * Закончившийся месяц остаётся `month=` и остаётся озаглавленным «Июль 2026»: резать в
 * нём нечего.
 *
 * ## Период целиком в будущем
 *
 * Оставляется как есть. Отрезать нечего — впереди весь период, а подменять его днём,
 * которого человек не просил, хуже, чем ответить на заданный вопрос. Пресетами такой
 * период не собирается (все они кончаются сегодня), только руками в двух полях даты.
 */
export function clampFuture(period: Period, now: Date = new Date()): Period {
  const today = isoDate(now);
  const {from, to} = periodBounds(period);
  if (to <= today) {
    return period;
  }
  if (from > today) {
    return period;
  }
  return {kind: 'range', from, to: today};
}

/**
 * Период, каким его можно ОТПРАВИТЬ: без будущего и в пределах серверного потолка.
 *
 * Единственное, что должны звать сборщики запросов, — иначе один маршрут обрежет
 * будущее, а соседний нет, и две цифры за «этот месяц» разойдутся, объяснить это будет
 * нечем.
 *
 * Порядок не случаен: сперва отрезается будущее (двигает КОНЕЦ), потом длина (двигает
 * НАЧАЛО). Наоборот — и 366-дневное окно считалось бы от конца, которого не будет.
 */
export function requestPeriod(period: Period, now: Date = new Date()): Period {
  return clampPeriod(clampFuture(period, now));
}

/** Every date in a period, in order — the x-axis of any per-day chart. */
export function eachDay(period: Period): string[] {
  const {from, to} = periodBounds(period);
  const out: string[] = [];
  const cursor = parseIsoDate(from);
  const last = parseIsoDate(to);
  // Bounded by the same 366-day ceiling the server enforces, so this cannot run away
  // even if handed a nonsense range.
  for (let i = 0; cursor <= last && i < MAX_PERIOD_DAYS; i += 1) {
    out.push(isoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}
